import React, { useEffect, useMemo } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { Box, CenteredLayout, ContentLayoutF, H1 } from '@betterment/samba-react';
import { useNotifications } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import * as Sentry from '@sentry/browser';

import { AccountDetailsStateSwitch } from './account-detail/AccountDetailsStateSwitch';
import { calculateMoneyAmount, isSellingToUnderweight } from './calculations';
import { CreateFinancialSecurityOrderDocument } from './graphql/mutation.createFinancialSecurityOrder.generated';
import { CreateFinancialSecuritySellOrderTaxImpactPreviewDocument } from './graphql/mutation.createFinancialSecuritySellOrderTaxImpactPreview.generated';
import { EstimatedTradeTimingDocument } from './graphql/query.estimatedTradeTiming.generated';
import { PurchaseOrderAccountHoldingsDocument } from './graphql/query.purchaseOrderaccountHoldings.generated';
import { Header } from './Header';
import { ReviewOrderDetails } from './ReviewOrderDetails';
import { ReviewOrderDisclaimer } from './ReviewOrderDisclaimer';
import { useSellOrderFlowSessionStorageKey } from './SellOrderFlow';
import { TaxImpactPreviewStateSwitch } from './tax-impact-preview/TaxImpactPreviewStateSwitch';
import { ReviewOrderStepProps, toAmountChoice, toSubmittableOrderValue } from './types';
import { UnderweightWarning } from './UnderweightWarning';

import { FinancialSecurityOrderRequestDirection } from '../../../graphql/schema';
import { WorkflowPage } from '../../../Shared/Components/WorkflowPage';
import { MissingPreviewDataBanner } from '../../MissingPreviewDataBanner/MissingPreviewDataBanner';
import { PendingTransactionsBanner } from '../../pending-transactions-banner/PendingTransactionsBanner';

export const ReviewOrder: React.FC<ReviewOrderStepProps> = ({ flowState, goToStep, navigate }) => {
  const accountId = useRequiredParam('accountId');
  const sessionStorageKey = useSellOrderFlowSessionStorageKey();
  const {
    values: { unit, amount, coordinatedAccount, financialSecurityId, pricePerShare, sellAll },
  } = flowState;

  const accountHoldingsResult = useGraphQLQuery(PurchaseOrderAccountHoldingsDocument, {
    variables: {
      managedAccountId: accountId,
      subAccountId: coordinatedAccount.id,
    },
  });

  const tradeTimingResult = useGraphQLQuery(EstimatedTradeTimingDocument, {
    variables: { financialSecurityId },
  });

  const [createFinancialSecurityOrder, submitOrderMutationResult] = useGraphQLMutation(
    CreateFinancialSecurityOrderDocument
  );

  const [createTaxImpactPreview, previewMutationResult] = useGraphQLMutation(
    CreateFinancialSecuritySellOrderTaxImpactPreviewDocument
  );

  const { showNotification } = useNotifications();

  const submittableOrderValue = useMemo(() => toSubmittableOrderValue(toAmountChoice(unit, amount)), [unit, amount]);

  const previewId = previewMutationResult.data?.createFinancialSecuritySellOrderTaxImpactPreview?.result?.id;

  const taxImpactPreviewInput = useMemo(
    () => ({
      financialSecurityId,
      subAccountId: coordinatedAccount.id,
      value: submittableOrderValue,
    }),
    [coordinatedAccount.id, financialSecurityId, submittableOrderValue]
  );

  const orderInput = {
    subAccountId: coordinatedAccount.id,
    financialSecurityId,
    direction: FinancialSecurityOrderRequestDirection.Sell,
    value: submittableOrderValue,
    sellAll,
    sharePriceCents: pricePerShare.cents,
    previewId,
  };

  useEffect(() => {
    void createTaxImpactPreview({
      variables: {
        input: taxImpactPreviewInput,
      },
    });
  }, [taxImpactPreviewInput, createTaxImpactPreview]);

  const onCancel = () => {
    void navigate(flowState.returnToPath);
  };

  const onBack = () => {
    goToStep('new', flowState.values);
  };

  const onComplete = (sessionKey: string) => {
    sessionStorage.removeItem(`bmt.${sessionKey}`);

    showNotification({ type: 'success', message: 'Your trade order has been submitted.' });
    void navigate(flowState.returnToPath);
  };

  const handleSubmit = async () => {
    try {
      const response = await createFinancialSecurityOrder({
        variables: {
          input: orderInput,
        },
      });

      if (response.data?.createFinancialSecurityOrderRequest?.result) {
        onComplete(sessionStorageKey);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const hasMissingData = () => {
    if (
      !previewMutationResult.loading &&
      (accountHoldingsResult.error ||
        previewMutationResult.error ||
        !previewMutationResult.data?.createFinancialSecuritySellOrderTaxImpactPreview?.result)
    ) {
      return true;
    }
    if (!tradeTimingResult.loading && (tradeTimingResult.error || !tradeTimingResult.data)) {
      return true;
    }
    return false;
  };

  const hasMissingPreviewData = hasMissingData();
  const account = accountHoldingsResult.data?.managedAccount;
  const hasTransactionsPending = account?.subAccount?.hasPendingTransactions;
  const isUnderweightWarning = isSellingToUnderweight({
    securityGroupPositions: account?.securityGroupPositions ?? [],
    financialSecurityId,
    sellAmount: calculateMoneyAmount(flowState.values),
  });

  return (
    <WorkflowPage>
      <Header onCancel={onCancel} onBack={onBack}>
        Sell order
      </Header>
      <WorkflowPage.Main css={{ pt: 'space.400' }}>
        <CenteredLayout
          size="full"
          css={{
            rowGap: 'space.300',
            alignItems: 'stretch',
          }}
        >
          {(isUnderweightWarning || hasTransactionsPending || hasMissingPreviewData) && (
            <Box css={{ display: 'flex', flexDirection: 'column', rowGap: 'space.200' }}>
              {isUnderweightWarning && <UnderweightWarning />}
              {hasTransactionsPending && <PendingTransactionsBanner />}
              {hasMissingPreviewData && <MissingPreviewDataBanner />}
            </Box>
          )}
          <H1 variant="title400">Review sell order</H1>
          <ContentLayoutF>
            <ContentLayoutF.SlotContainer>
              <ContentLayoutF.Slot1 css={{ display: 'flex', flexDirection: 'column', gap: 'space.300' }}>
                <AccountDetailsStateSwitch
                  accountHoldingsResult={accountHoldingsResult}
                  subAccountName={coordinatedAccount.name}
                />
                <ReviewOrderDetails {...flowState.values} willSellAll={sellAll} tradeTimingResult={tradeTimingResult} />
              </ContentLayoutF.Slot1>

              <ContentLayoutF.Slot2 css={{ display: 'flex', flexDirection: 'column', gap: 'space.300' }}>
                <TaxImpactPreviewStateSwitch
                  previewResult={previewMutationResult}
                  accountHoldingsResult={accountHoldingsResult}
                  submitResult={submitOrderMutationResult}
                  onSubmit={() => void handleSubmit()}
                />
                <ReviewOrderDisclaimer />
              </ContentLayoutF.Slot2>
            </ContentLayoutF.SlotContainer>
          </ContentLayoutF>
        </CenteredLayout>
      </WorkflowPage.Main>
    </WorkflowPage>
  );
};
